import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  effect,
  untracked,
  Input,
  ChangeDetectionStrategy
} from "@angular/core";
// Translate
import { TranslateModule } from "@ngx-translate/core";
// App services
import { SessionDataService } from "../../../../services/shared/SessionDataService.service";
// Components
import { ScrollingModule } from "@angular/cdk/scrolling";
import { FormsModule } from "@angular/forms";
import { Client } from "../../../../interfaces/ClientGlobalInterface.type";
import { Entity } from "../../../../interfaces/EntityGlobalInterface.type";
import { Agrupation } from "../../../../interfaces/AgrupationGlobalInterface.type";
import { NavbarTopNavigationTreeComponent } from "./navbar-top-navigation-tree/navbar-top-navigation-tree.component";

/**
 * Text-filterable client > entity > agrupation list for switching context from the top navbar.
 * Works on a sorted copy of the session client list and renders it through the navigation tree.
 */
@Component({
  selector: "app-navbar-top-search-navigation",
  imports: [FormsModule, ScrollingModule, TranslateModule, NavbarTopNavigationTreeComponent],
  templateUrl: "./navbar-top-search-navigation.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./navbar-top-search-navigation.component.scss"
})
export class NavbarTopSearchNavigationComponent implements OnInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Session data
  clientList: Client[];
  update: boolean;
  someClientIncludes: boolean;

  // Options
  @Input() responsive: boolean = false;
  @ViewChild("navigationFilter") navigationFilter: ElementRef;
  filterText: string;

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(private readonly SessionDataService: SessionDataService) {
    effect(() => {
      const clientList = this.SessionDataService.clientList();
      if (clientList !== null) {
        this.clientList = clientList ? JSON.parse(JSON.stringify(clientList)) : null;
        if (this.clientList) {
          this.sortClientList();
        }
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    let clientList = untracked(() => this.SessionDataService.clientList());
    this.clientList = clientList
      ? JSON.parse(JSON.stringify(untracked(() => this.SessionDataService.clientList())))
      : null;
    if (this.clientList) {
      this.sortClientList();
    }
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Sort clients
  sortClientList(): void {
    this.clientList.sort((a, b) => a.clientName.localeCompare(b.clientName));
    this.clientList.forEach(client => {
      client.entityList.sort((a, b) => a.entity.localeCompare(b.entity));
      client.entityList.forEach(entity => {
        entity.agrupations.sort((a, b) => a.name.localeCompare(b.name));
      });
    });
  }

  // Show clients, entities and agrupations matching the filter text
  dropdownFilter(): void {
    let nullValue = this.filterText == null || this.filterText == "";
    let filterParsed = this.filterText.toLowerCase();
    this.someClientIncludes = false;
    // Check client
    this.clientList.forEach(client => {
      // Check entity
      let someEntityIncludes = false;
      client.entityList.forEach(entity => {
        // Check agrupation
        let someAgrupationIncludes = false;
        entity.agrupations.forEach(agrupation => {
          if (
            nullValue ||
            agrupation.name.toLowerCase().includes(filterParsed) ||
            agrupation.alias?.toLowerCase().includes(filterParsed)
          ) {
            agrupation["hidden"] = false;
            someAgrupationIncludes = true;
          } else {
            agrupation["hidden"] = true;
          }
        });
        // Check entity
        if (
          nullValue ||
          someAgrupationIncludes ||
          entity.entity.toLowerCase().includes(filterParsed) ||
          entity.alias?.toLowerCase().includes(filterParsed)
        ) {
          entity["hidden"] = false;
          someEntityIncludes = true;
        } else {
          entity["hidden"] = true;
        }
      });
      // Check client
      if (nullValue || someEntityIncludes) {
        client["hidden"] = false;
        this.someClientIncludes = true;
      } else {
        client["hidden"] = true;
      }
    });
    this.update = !this.update;
  }

  // Send the chosen agrupation (or entity) to SessionDataService, then clear the filter
  updateNavigation(option?: {
    client: Client;
    entity: Entity;
    agrupation: Agrupation;
    visible: boolean;
  }): void {
    if (option?.agrupation) {
      this.SessionDataService.sendAgrupation(option.agrupation);
    } else if (option?.entity) {
      this.SessionDataService.sendEntity(option.entity);
    }
    this.resetSearchBoxText();
  }

  // Reset search box text
  resetSearchBoxText(): void {
    this.filterText = null;
    if (this.navigationFilter) {
      this.navigationFilter.nativeElement.value = "";
    }
  }
}
